import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getEdgeSettingsPaths } from '@app/utils/appContext';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Checkbox,
  Form,
  FormGroup,
  FormHelperText,
  PageSection,
  Radio,
  Stack,
  StackItem,
  TextArea,
  TextInput,
  Title,
} from '@patternfly/react-core';
import { getRepositoryById } from '../data/repositoryData';

const EditRepository: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const repository = id ? getRepositoryById(id) : undefined;

  const [repositoryName, setRepositoryName] = React.useState(repository?.name || '');
  const [registryHostname, setRegistryHostname] = React.useState('');
  const [repositoryType, setRepositoryType] = React.useState<'git' | 'http' | 'oci'>('oci');
  const [scheme, setScheme] = React.useState<'https' | 'http'>('https');
  const [accessMode, setAccessMode] = React.useState<'readonly' | 'readwrite'>('readonly');
  const [useAdvancedConfig, setUseAdvancedConfig] = React.useState(true);
  const [useBasicAuth, setUseBasicAuth] = React.useState(true);
  const [username, setUsername] = React.useState('rh_ee_camadorg');
  const [password, setPassword] = React.useState('*****');
  const [skipServerVerification, setSkipServerVerification] = React.useState(false);
  const [caCertificate, setCaCertificate] = React.useState('');

  // Parse URL to extract registry hostname
  React.useEffect(() => {
    if (repository?.url) {
      try {
        const url = new URL(repository.url);
        setRegistryHostname(url.hostname + (url.port ? `:${url.port}` : ''));
        
        // Determine repository type from URL or existing type
        if (repository.type === 'Git repository') {
          setRepositoryType('git');
        } else if (repository.type === 'HTTP service') {
          setRepositoryType('http');
        } else if (repository.type === 'OCI registry') {
          setRepositoryType('oci');
        } else {
          setRepositoryType('oci');
        }

        // Set scheme
        setScheme(url.protocol === 'http:' ? 'http' : 'https');
      } catch (e) {
        // If URL parsing fails, try to extract hostname manually
        const match = repository.url.match(/https?:\/\/([^\/]+)/);
        if (match) {
          setRegistryHostname(match[1]);
        }
      }
    }
  }, [repository]);

  const handleCancel = () => {
    navigate(getEdgeSettingsPaths(window.location.pathname).repositories);
  };

  const handleSave = () => {
    // Handle save logic here
    console.log('Saving repository:', {
      repositoryName,
      registryHostname,
      repositoryType,
      scheme,
      accessMode,
      useAdvancedConfig,
      useBasicAuth,
      username,
      skipServerVerification,
      caCertificate,
    });
    handleCancel();
  };

  const isFormValid = repositoryName.trim() !== '' && registryHostname.trim() !== '' && 
    (!useBasicAuth || (username.trim() !== '' && password.trim() !== ''));

  if (!repository) {
    return (
      <PageSection>
        <Title headingLevel="h1" size="xl">Repository not found</Title>
      </PageSection>
    );
  }

  return (
    <>
      <PageSection hasBodyWrapper={false} style={{ paddingBottom: 0 }}>
        <div style={{ margin: 'var(--pf-t--global--spacer--md) var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
          <Breadcrumb style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}>
            <BreadcrumbItem to="#" onClick={(e) => { e.preventDefault(); handleCancel(); }}>
              Repositories
            </BreadcrumbItem>
            <BreadcrumbItem isActive>Edit repository</BreadcrumbItem>
          </Breadcrumb>
          <Title headingLevel="h1" size="xl">
            Edit repository
          </Title>
        </div>
      </PageSection>

      <PageSection hasBodyWrapper={false}>
        <div style={{ paddingLeft: '24px', paddingRight: '24px' }}>
          <Form style={{ maxWidth: '800px' }}>
          <Stack hasGutter>
            <StackItem>
              <FormGroup
                label="Repository name"
                isRequired
                fieldId="repository-name"
              >
                <TextInput
                  id="repository-name"
                  value={repositoryName}
                  onChange={(_, value) => setRepositoryName(value)}
                  aria-describedby="repository-name-helper"
                />
                <FormHelperText id="repository-name-helper" hidden>
                  Enter a unique name for this repository
                </FormHelperText>
              </FormGroup>
            </StackItem>

            <StackItem>
              <FormGroup
                label="Registry hostname"
                isRequired
                fieldId="registry-hostname"
              >
                <TextInput
                  id="registry-hostname"
                  value={registryHostname}
                  onChange={(_, value) => setRegistryHostname(value)}
                  aria-describedby="registry-hostname-helper"
                />
                <FormHelperText id="registry-hostname-helper">
                  For example: quay.io, registry.redhat.io, myregistry.com:5000
                </FormHelperText>
              </FormGroup>
            </StackItem>

            <StackItem>
              <FormGroup
                label="Repository type"
                fieldId="repository-type"
                isRequired
              >
                <Stack hasGutter>
                  <StackItem>
                    <Radio
                      id="repo-type-git"
                      name="repository-type"
                      label="Git repository"
                      isChecked={repositoryType === 'git'}
                      onChange={() => setRepositoryType('git')}
                    />
                  </StackItem>
                  <StackItem>
                    <Radio
                      id="repo-type-http"
                      name="repository-type"
                      label="HTTP service"
                      isChecked={repositoryType === 'http'}
                      onChange={() => setRepositoryType('http')}
                    />
                  </StackItem>
                  <StackItem>
                    <Radio
                      id="repo-type-oci"
                      name="repository-type"
                      label="OCI registry"
                      isChecked={repositoryType === 'oci'}
                      onChange={() => setRepositoryType('oci')}
                    />
                  </StackItem>
                </Stack>
              </FormGroup>
            </StackItem>

            <StackItem>
              <FormGroup
                label="Scheme"
                fieldId="scheme"
              >
                <Stack hasGutter>
                  <StackItem>
                    <Radio
                      id="scheme-https"
                      name="scheme"
                      label="HTTPS"
                      isChecked={scheme === 'https'}
                      onChange={() => setScheme('https')}
                    />
                  </StackItem>
                  <StackItem>
                    <Radio
                      id="scheme-http"
                      name="scheme"
                      label="HTTP"
                      isChecked={scheme === 'http'}
                      onChange={() => setScheme('http')}
                    />
                  </StackItem>
                </Stack>
              </FormGroup>
            </StackItem>

            <StackItem>
              <FormGroup
                label="Access mode"
                fieldId="access-mode"
              >
                <Stack hasGutter>
                  <StackItem>
                    <Radio
                      id="access-readonly"
                      name="access-mode"
                      label="View"
                      isChecked={accessMode === 'readonly'}
                      onChange={() => setAccessMode('readonly')}
                    />
                  </StackItem>
                  <StackItem>
                    <Radio
                      id="access-readwrite"
                      name="access-mode"
                      label="Manage"
                      isChecked={accessMode === 'readwrite'}
                      onChange={() => setAccessMode('readwrite')}
                    />
                  </StackItem>
                </Stack>
              </FormGroup>
            </StackItem>

            <StackItem style={{ marginBottom: '0px' }}>
              <FormGroup
                label="Advanced configurations"
                fieldId="advanced-configurations"
              >
              </FormGroup>
            </StackItem>

            <StackItem style={{ marginTop: 'calc(-1 * var(--pf-t--global--spacer--md))', marginBottom: '0px' }}>
              <Checkbox
                id="use-basic-auth"
                label="Basic authentication"
                isChecked={useBasicAuth}
                onChange={(_, checked) => setUseBasicAuth(checked)}
              />
            </StackItem>

            {useBasicAuth && (
              <>
                <StackItem style={{ paddingLeft: 'var(--pf-t--global--spacer--lg)' }}>
                  <FormGroup
                    label="Username"
                    isRequired
                    fieldId="username"
                  >
                    <TextInput
                      id="username"
                      value={username}
                      onChange={(_, value) => setUsername(value)}
                    />
                  </FormGroup>
                </StackItem>

                <StackItem style={{ paddingLeft: 'var(--pf-t--global--spacer--lg)' }}>
                  <FormGroup
                    label="Password"
                    isRequired
                    fieldId="password"
                  >
                    <TextInput
                      id="password"
                      type="password"
                      value={password}
                      onChange={(_, value) => setPassword(value)}
                    />
                  </FormGroup>
                </StackItem>
              </>
            )}

            <StackItem>
              <Checkbox
                id="skip-server-verification"
                label="Skip server verification"
                isChecked={skipServerVerification}
                onChange={(_, checked) => setSkipServerVerification(checked)}
              />
            </StackItem>

            <StackItem style={{ paddingLeft: '24px' }}>
              <FormGroup
                label="CA certificate"
                fieldId="ca-certificate"
              >
                <TextArea
                  id="ca-certificate"
                  value={caCertificate}
                  onChange={(_, value) => setCaCertificate(value)}
                  rows={6}
                  resizeOrientation="vertical"
                />
              </FormGroup>
            </StackItem>

            <StackItem>
              <div style={{ display: 'flex', gap: 'var(--pf-t--global--spacer--sm)', alignItems: 'center' }}>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  isDisabled={!isFormValid}
                >
                  Save
                </Button>
                <Button
                  variant="link"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </div>
            </StackItem>
          </Stack>
        </Form>
        </div>
      </PageSection>
    </>
  );
};

export { EditRepository };

